import React, {
  HTMLAttributes,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';

import {
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import {
  type ImmutableEntity,
} from '@uncinc/react-drupal-core/types';

import { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  Breadcrumbs,
  ParagraphsRenderer,
  MoreLikeThis,
} from '../../../../components';

import {
  blendHexColors,
  setPrimaryColor,
  setPrimaryColorOpaque,
  setSecondaryColor,
} from '../../../../helpers/colors';
import {
  BLACK,
  WHITE,
  GRAY,
} from '../../../../config/constants';

import './style.scss';
import IntroductionComponent from './IntroductionComponent';

interface NodeProjectPageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<NodeProject>;
}

const NodeProjectPage: React.FC<NodeProjectPageProps> = ({ entity }) => {
  const classes = classNames('Node Node-type-breadcrumbs NodeProjectPage');
  const project = entity.toJS();
  const title = project.title[0].value;
  const labels = project.field_labels;
  const media = project.field_media_item[0];
  const body = project.body[0]?.processed;
  const themes = project.field_themes;

  let posterImage;
  if (media.bundle === 'video') {
    posterImage = media.entity.field_poster_image[0]?.url;
  }

  const primaryColor = project.field_color_primary[0]?.value || WHITE;
  const secondaryColor = project.field_color_secondary[0]?.value || BLACK;

  const hero = useRef<HTMLDivElement>(null);
  const heroHeight = useRef(1000);

  const handleScroll = () => {
    const scrollProgress = (heroHeight.current - window.scrollY) / 1000;
    const ratio = Math.max(0, Math.min(1, 1 - (0.3 + scrollProgress)));
    const blendedColor = blendHexColors(primaryColor, WHITE, ratio);
    setPrimaryColorOpaque(blendedColor);
  };

  useEffect(() => {
    setPrimaryColor(primaryColor);
    setPrimaryColorOpaque(primaryColor);
    setSecondaryColor(secondaryColor);
    // comment this line out when on local if
    // working on firefox and it's necessary to view the elements tab in the inspector
    // see "Firefox scrolling performance issues" section in README for explanation
    window.addEventListener('scroll', handleScroll);

    if (hero.current) {
      // @ts-ignore
      const backgroundHeight: number = hero.current.getBoundingClientRect().height;
      heroHeight.current = backgroundHeight > 1000 ? backgroundHeight : 1000;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      setPrimaryColor(WHITE);
      setPrimaryColorOpaque(WHITE);
      setSecondaryColor(GRAY);
    };
  });

  return (
    <article
      itemType="http://schema.org/WebPage"
      className={classes}
    >
      <div
        ref={hero}
        className="NodeProjectPage__background"
      >
        <Container>
          <div className="row">
            <div className="col-lg-8 col-md-10 col-12">
              <div className="NodeProjectPage__hero">
                <div className="NodeProjectPage__hero__text">
                  <Breadcrumbs />
                  <h1 itemProp="name">{title}</h1>
                  {body && (
                    <WysiwygField
                      property="schema:text"
                      className="text--large"
                      html={body}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
        <Container>
          <div className="row">
            <div className="col-12">
              <div className="NodeProjectPage__hero__image">
                <DynamicMediaItem
                  media={media}
                  imageAspectRatio="16_9"
                  {...posterImage && {
                    posterImage,
                  }}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>

      <IntroductionComponent
        paragraphs={entity.get('field_project_introduction') as any}
        sidebarProps={{
          field_themes: project.field_themes as any,
          field_partner: project.field_partner as any,
          field_type: project.field_type as any,
          field_expertises: project.field_expertises as any,
          field_technologies: project.field_technologies as any,
          field_link: project.field_link as any,
        }}
      />

      <ParagraphsRenderer
        className="FieldParagraphs"
        paragraphs={entity.get('field_paragraphs')}
        exclude={['cta_wrapper']}
      />

      <ParagraphsRenderer
        paragraphs={entity.get('field_paragraphs')}
        only={['cta_wrapper']}
      />

      <MoreLikeThis
        labels={labels}
        themes={themes}
        entity={project}
      />

    </article>
  );
};

export default NodeProjectPage;
